import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { ShoppingCart, Calendar, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { differenceInDays } from 'date-fns';
import { AlertPreference } from '@/api/entities';
import { useTranslation } from '@/components/utils/translations';
import { categoryMatches, getDealCategories } from '@/utils/procurementCategoryUtils';
import { expandAlertCategoriesForMatching } from '@/lib/alertCategoryNames';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';

const calculateDaysLeft = (deadlineString) => {
  if (!deadlineString || deadlineString === 'N/A' || deadlineString.trim() === '') return null;
  try {
    const deadline = new Date(deadlineString);
    if (isNaN(deadline.getTime())) return null;
    return differenceInDays(deadline, new Date());
  } catch (error) {
    console.error('Error calculating days left:', deadlineString, error);
    return null;
  }
};

// Add this helper function after calculateDaysLeft (around line 23)
const getDeadline = (item) => {
  return item.deadline_date || item.deadline || item.due_date || item.closing_date || item.submission_deadline || null;
};

const getSourceUrl = (item) => {
  return item.source_url || item.full_url || item.url || item.link || null;
};

export default function LiveDeals({ procurement, isLoading, currentTenant: currentTenantProp }) {
  const { t } = useTranslation();
  const contextUser = useUser();
  const currentTenant = useCurrentTenant() || currentTenantProp;
  const [alertPreferences, setAlertPreferences] = useState(null);
  const [loadingPrefs, setLoadingPrefs] = useState(true);

  const userId = contextUser?.id || null;
  const userRole = contextUser?.role || null;
  const resolvedTenant = resolveEffectiveTenant(
    userRole === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
    currentTenant,
    contextUser,
    'instructure'
  );

  useEffect(() => {
    const loadAlertPreferences = async () => {
      if (!userId) {
        setLoadingPrefs(false);
        return;
      }
      try {
        const prefs = await AlertPreference.filter({
          user_id: userId,
          tenant_id: resolvedTenant || 'instructure'
        });
        const userPrefs = prefs[0] || null;
        setAlertPreferences(userPrefs);
      } catch (error) {
        console.error('Error loading alert preferences:', error);
      } finally {
        setLoadingPrefs(false);
      }
    };

    loadAlertPreferences();
  }, [userId, resolvedTenant]);

  const filteredDeals = useMemo(() => {
    if (!procurement || procurement.length === 0) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const userCategories = expandAlertCategoriesForMatching(alertPreferences?.live_deal_categories || []);

    let deals = procurement.filter(p => p.status === 'Live');

    // Filter by category only when user has explicitly set preferences; otherwise show all (Market already filters by tenant)
    if (userCategories.length > 0) {
      const filtered = deals.filter(p => {
        const dealCats = getDealCategories(p);
        const fromMatched = (p.matched_categories || []).map((c) => (typeof c === 'string' ? c : c?.name || c?.category || '')).filter(Boolean).join('|');
        const matches = categoryMatches(userCategories, p) || (fromMatched && categoryMatches(userCategories, fromMatched));
        return matches || dealCats.length === 0;
      });
      if (filtered.length > 0) deals = filtered;
    }

    // Include deals with future deadlines OR no deadline (N/A) — match Live Radar behavior
    const filtered = deals.filter(p => {
      const deadline = getDeadline(p);
      if (!deadline || deadline === 'N/A' || (typeof deadline === 'string' && deadline.trim() === '')) return true;
      try {
        const deadlineDate = new Date(deadline);
        deadlineDate.setHours(0, 0, 0, 0);
        return deadlineDate >= today;
      } catch {
        return true;
      }
    });

// Sort: deals with deadlines first (earliest first), then deals without deadlines
const sorted = filtered.sort((a, b) => {
  const aDeadline = getDeadline(a);
  const bDeadline = getDeadline(b);
  const aHasDeadline = aDeadline && aDeadline !== 'N/A' && (typeof aDeadline !== 'string' || aDeadline.trim() !== '');
  const bHasDeadline = bDeadline && bDeadline !== 'N/A' && (typeof bDeadline !== 'string' || bDeadline.trim() !== '');

  if (aHasDeadline && !bHasDeadline) return -1;
  if (!aHasDeadline && bHasDeadline) return 1;
  if (!aHasDeadline && !bHasDeadline) return 0;

  try {
    return new Date(aDeadline) - new Date(bDeadline);
  } catch {
    return 0;
  }
});
  
    return sorted;
  }, [procurement, alertPreferences]);

  return (
    <Card className="border-0 shadow-md h-full flex flex-col">
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-[#00C2C2]" />
            <CardTitle className="text-lg font-semibold text-[#0A1A2F]">{t('liveDealsTitle')}</CardTitle>
          </div>
          <Link to={createPageUrl('Procurement')} className="text-xs font-medium text-[#00C2C2] hover:text-[#00A3A3]">{t('viewAll')}</Link>
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-hidden flex flex-col">
        {isLoading || loadingPrefs ? (
          <div className="space-y-3">
            {Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        ) : filteredDeals.length > 0 ? (
          <div className="space-y-3 overflow-y-auto pr-2" style={{ maxHeight: '400px' }}>
            {filteredDeals.map((item, idx) => {
               const deadline = getDeadline(item);
               const daysLeft = calculateDaysLeft(deadline);
               const hasDeadline = deadline && deadline !== 'N/A' && (typeof deadline !== 'string' || deadline.trim() !== '');
              return (
                <div key={`${item.id}-${idx}`} className="p-3 rounded-lg bg-gray-50/70 hover:bg-[#00C2C2]/10 transition-colors">
                  <div className="flex flex-col gap-1">
                    <h3 className="text-sm font-semibold text-[#0A1A2F] truncate pr-2">
                      {getSourceUrl(item) ? (
                        <a
                          href={getSourceUrl(item)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#0A1A2F] hover:text-[#00C2C2] underline transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {item.title || '—'}
                        </a>
                      ) : (
                        item.title || '—'
                      )}
                    </h3>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-[#5C6B7A] truncate pr-2">{item.account_name || item.institution_name || item.institution}</p>
                    {!hasDeadline ? (
                      <Badge variant="outline" className="text-xs bg-gray-100 text-gray-700 border-gray-300 flex-shrink-0">
                        {t('noDeadline')}
                      </Badge>
                    ) : daysLeft !== null ? (
                      daysLeft >= 0 ? (
                        <Badge 
                          variant="outline" 
                          className={`text-xs flex-shrink-0 ${
                            daysLeft <= 10 
                              ? 'text-red-600 border-red-200 bg-red-50' 
                              : 'text-orange-600 border-orange-200 bg-orange-50'
                          }`}
                        >
                          {daysLeft <= 10 && <AlertTriangle className="w-3 h-3 mr-1" />}
                          <Calendar className="w-3 h-3 mr-1" />
                          {daysLeft}{t('daysLeft')}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs text-red-600 border-red-200 bg-red-50 flex-shrink-0">
                          {t('expired')}
                        </Badge>
                      )
                    ) : (
                      <Badge variant="outline" className="text-xs bg-gray-100 text-gray-700 border-gray-300 flex-shrink-0">
                        {t('noDeadline')}
                      </Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-center text-sm text-[#5C6B7A] py-8">
            {alertPreferences?.live_deal_categories?.length > 0
              ? t('noLiveDealsCategories')
              : t('noLiveDealsFound')}
          </p>
        )}
      </CardContent>
    </Card>
  );
}